import { useState, type ChangeEvent, type DragEvent } from 'react';
import { parsearCatalogoCsv, type ResultadoParseoCatalogo } from '@tanteo/compartido';
import { importarProductos } from '../../lib/api-catalogo';
import { Boton, BotonArchivo, CeldaPlata, FilaAcciones, FilaTabla, MensajeError } from '../../ui/base';
import { Modal } from '../../ui/Modal';

interface Props {
  negocioId: string;
  abierto: boolean;
  onCerrar: () => void;
  onImportado: (cantidad: number) => void;
}

export function ImportarCsv({ negocioId, abierto, onCerrar, onImportado }: Props) {
  const [parseo, setParseo] = useState<ResultadoParseoCatalogo | null>(null);
  const [nombreArchivo, setNombreArchivo] = useState('');
  const [arrastrando, setArrastrando] = useState(false);
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);

  function reiniciar() {
    setParseo(null);
    setNombreArchivo('');
    setError('');
  }

  function cerrar() {
    reiniciar();
    onCerrar();
  }

  /**
   * El nombre del archivo y el resultado del parseo viven en el estado, no en el input: así el
   * control se limpia en cuanto se leyó y volver a elegir el mismo archivo dispara el cambio otra vez.
   */
  async function alArchivo(archivo: File | undefined, entrada?: HTMLInputElement) {
    if (entrada) entrada.value = '';
    if (!archivo) return;
    if (!/\.csv$/i.test(archivo.name)) {
      setError('El archivo tiene que ser un CSV. Desde Excel: «Guardar como → CSV UTF-8».');
      return;
    }
    setError('');
    setNombreArchivo(archivo.name);
    setParseo(parsearCatalogoCsv(await archivo.text()));
  }

  function alSoltar(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setArrastrando(false);
    void alArchivo(e.dataTransfer.files?.[0]);
  }

  async function importar() {
    if (!parseo || parseo.filas.length === 0) return;
    setOcupado(true);
    setError('');
    try {
      const r = await importarProductos(negocioId, parseo.filas);
      if (r.errores.length > 0) {
        setError(r.errores.join(' '));
        return;
      }
      reiniciar();
      onImportado(r.importados);
      onCerrar();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron importar los productos.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Modal abierto={abierto} titulo="Importar catálogo (CSV)" onCerrar={cerrar}>
      <div className="space-y-3">
        <p className="text-muted text-contenido">
          Sube un CSV con columnas <strong>sku, categoria, nombre, precio, impuestos</strong>. En «impuestos» va el
          nombre tal como está en Configuración; varios se separan con <strong>|</strong> (por ejemplo{' '}
          <em>IVA 19%|INC 8%</em>) y vacío deja el producto exento. Desde Excel: «Guardar como → CSV UTF-8». Los
          productos se crean o se actualizan por SKU.
        </p>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setArrastrando(true);
          }}
          onDragLeave={() => setArrastrando(false)}
          onDrop={alSoltar}
          className={`rounded-card border-2 border-dashed p-4 text-center ${
            arrastrando ? 'border-primary bg-primary/10' : 'border-border'
          }`}
        >
          <div className="flex flex-wrap items-center justify-center gap-2">
            <BotonArchivo
              variante="secundario"
              accept=".csv,text/csv"
              onChange={(e: ChangeEvent<HTMLInputElement>) => void alArchivo(e.target.files?.[0], e.target)}
              data-e2e="csv-elegir"
            >
              Seleccionar archivo
            </BotonArchivo>
            <span className="text-contenido font-semibold" data-e2e="csv-nombre">
              {nombreArchivo || 'Ningún archivo elegido'}
            </span>
          </div>
          <p className="text-muted mt-2 text-etiqueta">o arrastra el CSV aquí</p>
        </div>

        <MensajeError e2e="csv-error">{error}</MensajeError>

        {parseo && (
          <div className="space-y-2">
            <p className="text-contenido font-semibold">
              {parseo.filas.length} producto{parseo.filas.length === 1 ? '' : 's'} para importar
              {parseo.errores.length > 0 ? ` · ${parseo.errores.length} fila(s) con error` : ''}.
            </p>
            {parseo.errores.length > 0 && (
              <ul className="text-destructive max-h-32 overflow-y-auto text-contenido">
                {parseo.errores.map((er, i) => (
                  <li key={i}>
                    Fila {er.fila}: {er.mensaje}
                  </li>
                ))}
              </ul>
            )}
            {parseo.filas.length > 0 && (
              <div className="max-h-48 overflow-auto">
                <table className="w-full text-left text-contenido">
                  <thead>
                    <tr className="text-muted border-border border-b text-etiqueta uppercase">
                      <th className="py-1 pr-2">SKU</th>
                      <th className="py-1 pr-2">Nombre</th>
                      <th className="py-1 pr-2 text-right">Precio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parseo.filas.slice(0, 50).map((f) => (
                      <FilaTabla key={f.sku}>
                        <td className="num py-1 pr-2 font-bold">{f.sku}</td>
                        <td className="py-1 pr-2">{f.nombre}</td>
                        <CeldaPlata className="py-1">{f.precio}</CeldaPlata>
                      </FilaTabla>
                    ))}
                  </tbody>
                </table>
                {parseo.filas.length > 50 && (
                  <p className="text-muted mt-1 text-etiqueta">…y {parseo.filas.length - 50} más.</p>
                )}
              </div>
            )}
          </div>
        )}

        <FilaAcciones>
          <Boton variante="contorno" onClick={cerrar} disabled={ocupado}>
            Cancelar
          </Boton>
          <Boton onClick={() => void importar()} disabled={ocupado || !parseo || parseo.filas.length === 0}>
            {ocupado ? 'Importando…' : `Importar ${parseo?.filas.length ?? 0}`}
          </Boton>
        </FilaAcciones>
      </div>
    </Modal>
  );
}
